import React from 'react';
import { ArrowDown, Check, CircleDot, Database, MessageSquareText, ShieldCheck } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import { ClosingCta, PrimaryCta, Reveal, SectionIntro } from '../components/Alt4Shared';
import { OPERATING_LAYERS } from '../data/site';

const sharedContext = [
  ['Patient timeline', 'Every interaction and open task in sequence'],
  ['Practice state', 'Schedule, clinical, ledger, and communication context'],
  ['Operating rules', 'Permissions, policies, and location-level configuration'],
];

export default function Alt4Platform() {
  return (
    <Alt4Layout title="Platform">
        <section className="alt4-page-hero alt4-section">
          <div className="alt4-container alt4-hero-grid alt4-page-hero__grid">
            <Reveal className="alt4-hero-copy">
              <span className="alt4-eyebrow">The operating layer</span>
              <h1>One operating layer. Every workflow in context.</h1>
              <p>The AI works from the same schedule, chart, ledger, communications, and operating rules your teams use—because intelligence is native to the PMS, not layered on top.</p>
              <PrimaryCta />
            </Reveal>

            <Reveal className="alt4-platform-context alt4-panel" delay={0.08}>
              <header>
                <div><span className="alt4-interface-label">Unified patient context</span><h2>One live record of the work</h2></div>
                <span className="alt4-status alt4-status--running"><CircleDot aria-hidden="true" /> Live</span>
              </header>
              <div className="alt4-platform-context__subject">
                <span>Tomorrow · 9:30 AM</span>
                <strong>Restorative visit</strong>
                <small>Preparation is running across the practice</small>
              </div>
              <div className="alt4-platform-context__sources">
                {sharedContext.map(([title, detail]) => (
                  <article key={title}>
                    <Check aria-hidden="true" />
                    <div><strong>{title}</strong><span>{detail}</span></div>
                  </article>
                ))}
              </div>
              <div className="alt4-platform-context__action">
                <ShieldCheck aria-hidden="true" />
                <span><small>Authorized next step</small><strong>Complete readiness workflow</strong></span>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="alt4-section">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="One system of action"
              title="The context to understand. The surfaces to act."
              body="Each operating layer reads and updates the same underlying practice state. A handoff in one part of the office becomes usable context everywhere the work continues."
            />
            <div className="alt4-operating-layers alt4-grid">
              {OPERATING_LAYERS.map((layer, index) => (
                <Reveal className="alt4-card alt4-operating-layer" delay={index * 0.04} key={layer.title}>
                  <span className="alt4-operating-layer__number">0{index + 1}</span>
                  <h3>{layer.title}</h3>
                  <p>{layer.description}</p>
                  <span className="alt4-operating-layer__detail">{layer.detail}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="alt4-section alt4-section--pale">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-platform-continuity">
            <SectionIntro
              eyebrow="Continuous context"
              title="A workflow can cross departments without starting over."
              body="A schedule change can update preparation, communications, and downstream tasks from the same patient and practice state. The activity trail stays intact from event to outcome."
            />
            <div className="alt4-panel alt4-context-pipeline" aria-label="Shared context flowing into one action layer">
              <div className="alt4-context-pipeline__sources">
                <span><Database aria-hidden="true" /> Practice core</span>
                <span><MessageSquareText aria-hidden="true" /> Communications</span>
                <span><CircleDot aria-hidden="true" /> Live operations</span>
              </div>
              <ArrowDown aria-hidden="true" />
              <div className="alt4-context-pipeline__core">
                <span>Shared context</span>
                <strong>Understand → decide → act → update</strong>
              </div>
              <ArrowDown aria-hidden="true" />
              <div className="alt4-context-pipeline__outcome"><Check aria-hidden="true" /> Outcome recorded across the PMS</div>
            </div>
          </div>
        </section>

        <ClosingCta
          eyebrow="See the operating layer"
          title="Bring the work and the intelligence into one system."
          body="Walk through how Asha connects practice context to coordinated action across locations."
        />
    </Alt4Layout>
  );
}
